<script setup lang="ts">
import { cloneDeep } from 'lodash';
import { useDialogPluginComponent } from 'quasar';
import { computed, ref } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n';

import { useLanguageValidationErrors } from '@/composables';
import { localizableString, localizableEditorString } from '@/helpers/validation.helper';
import type { ProjectModuleEventItemConfigDto, Locales, ProjectModuleDto } from '@/services/types/api.types';
import { useEventListItemsStore } from '@/stores/eventListItems.store';
import { wrapSavingOperation } from '@/helpers/notifications.helper';

import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import { provideEditLocale } from '@/composables/data-display-utils';

// STORE
const { t } = useI18n();
const eventListItemsStore = useEventListItemsStore();

// PROPS
const props = defineProps<{
  mode: 'add' | 'edit';
  eventItemConfig: ProjectModuleEventItemConfigDto;
  languageEnabled: Locales[];
  module: ProjectModuleDto;
}>();

// EMITS
defineEmits([...useDialogPluginComponent.emits]);

// CONST
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();
const initialValues = props.eventItemConfig;
const { handleSubmit, errors } = useForm({
  initialValues: initialValues,
  validationSchema: yup.object<ProjectModuleEventItemConfigDto>({
    title: localizableString(props.languageEnabled),
    description: localizableEditorString(props.languageEnabled),
  }),
});
const languageErrors = useLanguageValidationErrors(errors);
const editModel = ref(cloneDeep(props.eventItemConfig));
const editLocale = provideEditLocale(props.languageEnabled);

// COMPUTED
const dialogTitle = computed(() =>
  props.mode === 'add' ? t('project-module.event.dialogs.create') : t('project-module.event.dialogs.edit'),
);

// FUNCTIONS
const onSubmit = handleSubmit(async (values) => {
  // Only a project hosts an event block, so its parent is the project.
  await wrapSavingOperation(
    async () => await eventListItemsStore.saveEventItem(values, props.module.parent_id),
    () => onDialogOK(),
  );
});
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" v-if="editModel" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ dialogTitle }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-input
          name="title"
          :label="t('global.label.title')"
          :editLocale="editLocale"
          :language_enabled="languageEnabled"
          outlined
        />
        <q-field :label="t('global.label.description')" stack-label borderless>
          <template v-slot:control>
            <localized-editor
              name="description"
              :label="t('global.label.description')"
              class="q-mt-sm full-width"
              :editLocale="editLocale"
              :language_enabled="languageEnabled"
            />
          </template>
        </q-field>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
